import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useStore } from "../store";
import {
  LayoutDashboard,
  ListTodo,
  Package,
  Search,
  Users,
  Globe,
  Clock,
  ShoppingBag,
  ScrollText,
  Settings,
  Wifi,
  WifiOff,
  Bell,
  X,
} from "lucide-react";
import clsx from "clsx";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/tasks", icon: ListTodo, label: "Tasks" },
  { path: "/products", icon: Package, label: "Products" },
  { path: "/search", icon: Search, label: "Search" },
  { path: "/profiles", icon: Users, label: "Profiles" },
  { path: "/proxies", icon: Globe, label: "Proxies" },
  { path: "/queue", icon: Clock, label: "Queue" },
  { path: "/orders", icon: ShoppingBag, label: "Orders" },
  { path: "/logs", icon: ScrollText, label: "Logs" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const { connected, connectWebSocket, notifications, removeNotification } = useStore();
  const location = useLocation();

  useEffect(() => {
    connectWebSocket();
  }, [connectWebSocket]);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-dark-800/50 backdrop-blur-xl border-r border-dark-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-dark-700">
          <h1 className="font-display text-2xl font-bold tracking-wider">
            <span className="text-error">EZ</span><span className="text-white">POKE</span>
          </h1>
          <p className="text-xs text-gray-500 mt-1">Pokemon TCG Bot</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-primary-500/20 text-primary-500 border border-primary-500/30"
                    : "text-gray-400 hover:bg-dark-700 hover:text-gray-200"
                )
              }
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Connection Status */}
        <div className="p-4 border-t border-dark-700">
          <div className="flex items-center gap-2 text-sm">
            {connected ? (
              <>
                <Wifi size={16} className="text-success" />
                <span className="text-success">Connected</span>
              </>
            ) : (
              <>
                <WifiOff size={16} className="text-error" />
                <span className="text-error">Disconnected</span>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 bg-dark-800/30 border-b border-dark-700 flex items-center justify-between px-6">
          <h2 className="font-display text-lg font-semibold text-gray-200">
            {navItems.find((item) => item.path === location.pathname)?.label || "EZPoke"}
          </h2>

          {/* Notifications Bell */}
          <div className="relative">
            <Bell size={20} className="text-gray-400" />
            {notifications.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full text-[10px] flex items-center justify-center">
                {notifications.length}
              </span>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <Outlet />
        </div>

        {/* Notifications Toast */}
        {notifications.length > 0 && (
          <div className="fixed bottom-4 right-4 space-y-2 z-50">
            {notifications.slice(-3).map((notification) => (
              <div
                key={notification.id}
                className={clsx(
                  "p-4 rounded-lg shadow-lg border max-w-sm animate-in slide-in-from-right",
                  notification.type === "success" && "bg-success/20 border-success/50",
                  notification.type === "error" && "bg-error/20 border-error/50",
                  notification.type === "info" && "bg-info/20 border-info/50"
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-medium text-sm">{notification.title}</h4>
                    <p className="text-xs text-gray-400 mt-1">{notification.message}</p>
                  </div>
                  <button
                    onClick={() => removeNotification(notification.id)}
                    className="text-gray-400 hover:text-gray-200"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

